// frontend/src/components/Layout/Sidebar.jsx

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { menuAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import { FiChevronDown, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import './Sidebar.css';

const APP_VERSION = process.env.REACT_APP_APP_VERSION || '1.0.0';

const Sidebar = ({ collapsed, onToggle, menuItems: menuItemsProp, onMenuLoaded }) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { currentLanguage } = useTranslation();
  const [menuItems, setMenuItems] = useState(menuItemsProp || []);
  const [expandedMenus, setExpandedMenus] = useState([]);
  const [loading, setLoading] = useState(!menuItemsProp?.length);
  const [error, setError] = useState(null);

  // Sync from prop if Layout already loaded the menu
  useEffect(() => {
    if (menuItemsProp?.length) {
      setMenuItems(menuItemsProp);
      setLoading(false);
      autoExpand(menuItemsProp);
    }
  }, [menuItemsProp]);

  // Fallback: load independently if Layout didn't provide items
  useEffect(() => {
    if (!menuItemsProp?.length && isAuthenticated) loadMenuItems();
  }, [isAuthenticated, currentLanguage]);

  const loadMenuItems = async () => {
    try {
      setLoading(true);
      const response = await menuAPI.getAll(currentLanguage);
      if (response.data.success) {
        const items = response.data.menu;
        setMenuItems(items);
        onMenuLoaded?.(items);
        autoExpand(items);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading menu:', err);
      setError('Failed to load menu');
    } finally {
      setLoading(false);
    }
  };

  // Auto-expand any parent whose child matches current path
  const autoExpand = (items) => {
    const toExpand = [];
    const walk = (nodes, ancestors) => {
      for (const node of nodes) {
        const path = [...ancestors, node.id];
        if (node.path && (location.pathname === node.path || location.pathname.startsWith(node.path + '/'))) {
          toExpand.push(...ancestors.map(a => a)); // expand all ancestors
        }
        if (node.children?.length) walk(node.children, path);
      }
    };
    const walkParents = (nodes, parentIds = []) => {
      for (const node of nodes) {
        const isMatch = node.path && (location.pathname === node.path || location.pathname.startsWith(node.path + '/'));
        if (isMatch) toExpand.push(...parentIds);
        if (node.children?.length) walkParents(node.children, [...parentIds, node.id]);
      }
    };
    walkParents(items);
    if (toExpand.length) setExpandedMenus(toExpand);
  };

  const toggleMenu = (id) => {
    setExpandedMenus(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const isActive = (path, exact = false) => {
    if (!path) return false;
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Recursive renderer — handles levels 0, 1, 2, 3+
  const renderItem = (item, depth = 0) => {
    const hasChildren = item.children?.length > 0;
    const expanded = expandedMenus.includes(item.id);
    const active = isActive(item.path);

    if (hasChildren) {
      return (
        <div key={item.id} className={`nav-item-wrapper depth-${depth}`}>
          <div
            className={`nav-item has-children ${expanded ? 'expanded' : ''} ${active ? 'active' : ''}`}
            style={depth > 0 ? { paddingLeft: `${12 + depth * 14}px` } : {}}
            onClick={() => toggleMenu(item.id)}
          >
            <span className="nav-icon">{item.icon}</span>
            {!collapsed && (
              <>
                <span className="nav-label">{item.label}</span>
                <span className="nav-arrow">
                  {expanded ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}
                </span>
              </>
            )}
          </div>

          {!collapsed && expanded && (
            <div className={`nav-submenu depth-${depth}`}>
              {item.children.map(child => renderItem(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    // Leaf node
    if (!item.path) return null;
    return (
      <div key={item.id} className={`nav-item-wrapper depth-${depth}`}>
        <Link
          to={item.path}
          className={`nav-item ${isActive(item.path, true) ? 'active' : ''} ${depth > 0 ? 'nav-subitem' : ''}`}
          style={depth > 0 ? { paddingLeft: `${12 + depth * 14}px` } : {}}
        >
          <span className={depth > 0 ? 'nav-subicon' : 'nav-icon'}>{item.icon}</span>
          {!collapsed && <span className={depth > 0 ? 'nav-sublabel' : 'nav-label'}>{item.label}</span>}
        </Link>
      </div>
    );
  };

  const logoSection = (
    <div className="sidebar-header">
      <div className="sidebar-logo">
        {!collapsed && (
          <div>
            <img
              src={process.env.PUBLIC_URL + '/logo_impetus_black.svg'}
              alt="Logo"
              style={{ width: '100px', marginBottom: '10px' }}
            />
          </div>
        )}
      </div>
    </div>
  );

  if (loading) return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      {logoSection}
      <div className="sidebar-loading"><p>Loading menu...</p></div>
    </aside>
  );

  if (error) return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      {logoSection}
      <div className="sidebar-error">
        <p>{error}</p>
        <button onClick={loadMenuItems}>Retry</button>
      </div>
    </aside>
  );

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      {logoSection}

      <nav className="sidebar-nav">
        {menuItems.map((item) => renderItem(item, 0))}
      </nav>

<div className="sidebar-footer">
  <button
    type="button"
    className={`sidebar-version sidebar-version-btn ${collapsed ? 'is-collapsed' : ''}`}
    onClick={onToggle}
    title={collapsed ? 'Expand' : 'Collapse'}
  >
    <img
      src="/quantix-works-black-transparent.png"
      alt="QuantixWorks"
      className="sidebar-version-logo"
    />

    {!collapsed && (
      <>
        <span className="version-label"></span>
        <span className="version-number">{APP_VERSION}</span>
      </>
    )}
  </button>
</div>

    </aside>
  );
};

export default Sidebar;
